import React, { useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import createSagaMiddleware from 'redux-saga';

import rootReducer, { rootSaga } from './modules';
import { setAccount, setPlan, setToken, setUser } from './modules/auth';
import { finishLoaidngPreview, getContainerAsync, getContentsAsync, getEmailAsync, getFilesizeAsync, hideTokenWarning, historySave, initSidebar, requestSave, selectContent, setEmailId, showSidebar, showTokenWarning } from './modules/editor';

import postMsg from './lib/postMsg';
import { captureContents } from './util/captureScreenshot';

import { RootState } from './modules';

import EditorContainer from './containers/EditorContainer';
import LoginContainer from './containers/LoginContainer';

import './assets/css/index.scss';

const sagaMiddleware = createSagaMiddleware();

const store = createStore(
  rootReducer,
  // applyMiddleware(sagaMiddleware),
  composeWithDevTools(
    applyMiddleware(sagaMiddleware)
  )
);

sagaMiddleware.run(rootSaga);

type MatchParams = {
	emailId: string
};

function App() {
  const dispatch = useDispatch();
  const { contents } = useSelector((state: RootState) => state.editor);
  const emailId = useSelector((state: RootState) => state.editor.emailId);
  const isLoading = useSelector((state: RootState) => state.auth.account.id === '' || Object.keys(state.auth.plan).length === 0 || state.editor.email.id == -1 || (state.editor.email.subType === 4 && state.editor.testGroup == null));

  // 스크린샷 캡처 함수
  const captureScreenshot = async () => {
    try {
      // 현재 editor의 contents와 container 상태 가져오기
      const state = store.getState();
      const contents = state.editor.contents;
      const container = state.editor.container;

      // captureContents util 함수 사용 - 썸네일 이미지 생성
      const imageData = await captureContents(contents, container);

      // 썸네일 이미지와 템플릿 데이터를 함께 전송
      postMsg.send('screenshotData', {
        thumbnail: imageData,
        content: {
          data: contents,  // 템플릿 contents 데이터
          container: container  // 컨테이너 스타일 정보
        }
      });

    } catch (error) {
      console.error('Screenshot capture error:', error);
      postMsg.send('screenshotData', null);
    }
  };

const onMesssage = (emailId) => function (e){
  const { data, msg } = e.data;

  switch (msg) {
    case 'token':
      dispatch(setToken(data));
      break;
    case 'emailId':
      dispatch(setEmailId(data));
      break;
    case 'userInfo':
      dispatch(setUser(JSON.parse(data)));
      break;
    case 'accountInfo':
      dispatch(setAccount(JSON.parse(data)));
      break;
    case 'planInfo':
      dispatch(setPlan(JSON.parse(data)));
      break;
    case 'requestSave':
      dispatch(requestSave(data));
      break;
    case 'requestHistorySave':
      dispatch(historySave());
      break;
    case 'loadContent':
      console.log("### loadContent", emailId);
      loadContents(emailId);
      break;
    case 'unselectContent':
      dispatch(selectContent(-1));
      break;
    case 'openSidebar':
      dispatch(showSidebar());
      break;
    case 'finishLoaidngPreview':
      dispatch(finishLoaidngPreview());
      break;
    case 'getEditorContents':
      // 현재 에디터 컨텐츠 전송
      const state = store.getState();
      const currentContents = state.editor.contents;
      const currentContainer = state.editor.container;
      postMsg.send('editorContentsData', {
        contents: currentContents,
        container: currentContainer
      });
      break;
    case 'requestScreenshot':
      // 스크린샷 요청 처리
      captureScreenshot();
      break;
    case 'showTokenWarning':
      dispatch(showTokenWarning('연결을 시도하고 있습니다'));
      break;
    case 'hideTokenWarning':
      dispatch(hideTokenWarning());
      break;
    default:
      break;
  }
}

  useEffect(()=>{
    postMsg.send('boot');
    dispatch(initSidebar());
  },[]);

  useEffect(() => {
    console.log("### addEventListener", emailId);
    let handleFunction = onMesssage(emailId);
    window.addEventListener('message', handleFunction);
    return ()=>{
      console.log("#### remove function");
      window.removeEventListener('message', handleFunction);
    }
  }, [emailId]);

  useEffect(() => {
    if (contents?.length === 0 || isLoading) {
      return;
    }
    postMsg.send('contentsReady', contents?.length !== 0);
  }, [contents, isLoading])

  const loadContents = (emailId) => {
    dispatch(getEmailAsync.request(emailId));
		dispatch(getContentsAsync.request(emailId));
		dispatch(getContainerAsync.request(emailId));
		dispatch(getFilesizeAsync.request(emailId));
  }

  if (isLoading) return <></>;
  return (
    <BrowserRouter>
      <Switch>
        <Route exact path="/editor/:emailId" component={EditorContainer} />
        <Route exact path="/" component={LoginContainer} />
      </Switch>
    </BrowserRouter>
  );
}


const AppWrapper = () => {
  return (
    <Provider store={store}>
      <App />
    </Provider>
  )
}

export default AppWrapper;
